import { z } from 'zod';
import {
  currencyCodeSchema,
  isoDateSchema,
  longTextSchema,
  moneySchema,
  nonNegativeMoneySchema,
  positiveMoneySchema,
  shortTextSchema,
  uuidSchema,
} from '../primitives.js';
import { PAYMENT_METHODS } from '../enums.js';
import { investorFiguresSchema, linkCorrectionResultSchema } from './investors.js';

/**
 * Investments: what an investor has put money into, apart from who they are.
 *
 * One investor may hold several, of different kinds:
 *
 *   vehicle partnership   earnings under a linked vehicle agreement — a share
 *                         of each linked vehicle's remittance, through its
 *                         dated rider assignments
 *   equity                ownership of a percentage of a named entity. Owning
 *                         equity earns nothing by itself: money reaches the
 *                         holder only through a dividend somebody declares
 *   profit sharing        an agreed percentage of a profit defined in the
 *                         agreement: which income, which expenses, where, per
 *                         period, and what happens to a loss. Never ownership.
 */

export const INVESTMENT_TYPES = ['vehicle_partnership', 'equity', 'profit_sharing'] as const;
export type InvestmentType = (typeof INVESTMENT_TYPES)[number];

export const INVESTMENT_TYPE_LABELS: Record<InvestmentType, string> = {
  vehicle_partnership: 'Vehicle partnership',
  equity: 'Equity',
  profit_sharing: 'Profit sharing',
};

/** The one-line explanation shown wherever the kind is chosen or shown. */
export const INVESTMENT_TYPE_EXPLANATIONS: Record<InvestmentType, string> = {
  vehicle_partnership: 'Vehicle partnership: earnings under a linked vehicle agreement.',
  equity: 'Equity: ownership in the specified entity.',
  profit_sharing: 'Profit sharing: an agreed share of defined profit.',
};

export const INVESTMENT_STATUSES = ['draft', 'active', 'suspended', 'ended'] as const;
export type InvestmentStatus = (typeof INVESTMENT_STATUSES)[number];
export const INVESTMENT_STATUS_LABELS: Record<InvestmentStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  suspended: 'Suspended',
  ended: 'Ended',
};

export const INVESTMENT_SCOPE_KINDS = ['worldwide', 'country', 'state', 'region'] as const;
export type InvestmentScopeKind = (typeof INVESTMENT_SCOPE_KINDS)[number];
export const INVESTMENT_SCOPE_LABELS: Record<InvestmentScopeKind, string> = {
  worldwide: 'Worldwide',
  country: 'Country',
  state: 'State',
  region: 'Region',
};

export const VEHICLE_PAYOUT_BASES = ['fixed_per_period', 'percent_of_remittance'] as const;
export type VehiclePayoutBasis = (typeof VEHICLE_PAYOUT_BASES)[number];
export const VEHICLE_PAYOUT_BASIS_LABELS: Record<VehiclePayoutBasis, string> = {
  fixed_per_period: 'Fixed amount per vehicle each remittance week',
  percent_of_remittance: 'Percentage of each week’s remittance charged on the vehicle',
};

export const CALCULATION_PERIODS = ['weekly', 'monthly', 'quarterly', 'yearly'] as const;
export type CalculationPeriod = (typeof CALCULATION_PERIODS)[number];
export const PAYOUT_FREQUENCIES = ['weekly', 'monthly', 'quarterly', 'yearly', 'on_declaration'] as const;
export type PayoutFrequency = (typeof PAYOUT_FREQUENCIES)[number];
export const FREQUENCY_LABELS: Record<PayoutFrequency, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
  on_declaration: 'When declared',
};

/**
 * Income a profit share can be calculated on — the company's own revenue, as
 * Finance → Company earnings counts it. Remittance is the company's share
 * only: an investor's vehicle share is a payable, never profit.
 */
export const PROFIT_INCOME_STREAMS = ['delivery_commission', 'remittance_company', 'other_sales', 'loan_income'] as const;
export type ProfitIncomeStream = (typeof PROFIT_INCOME_STREAMS)[number];
export const PROFIT_INCOME_LABELS: Record<ProfitIncomeStream, string> = {
  delivery_commission: 'Delivery commission',
  remittance_company: 'Remittance (company share received)',
  other_sales: 'Other sales',
  loan_income: 'Loan interest and fees',
};

export const DEDUCTIBLE_EXPENSE_MODES = ['all', 'selected', 'none'] as const;
export type DeductibleExpenseMode = (typeof DEDUCTIBLE_EXPENSE_MODES)[number];
export const DEDUCTIBLE_EXPENSE_LABELS: Record<DeductibleExpenseMode, string> = {
  all: 'All business expenses (riders’ pay excluded, as in Finance)',
  selected: 'Only the expense categories selected',
  none: 'No expenses deducted',
};

export const LOSS_TREATMENTS = ['carry_forward', 'no_carry_forward'] as const;
export type LossTreatment = (typeof LOSS_TREATMENTS)[number];
export const LOSS_TREATMENT_LABELS: Record<LossTreatment, string> = {
  carry_forward: 'Carry a loss forward: later profit pays it off before any share is due',
  no_carry_forward: 'No carry-forward: each period stands alone; a loss period pays nothing',
};

// ── Where an investment applies ────────────────────────────────────────────

const countryCode = z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/, 'Expected a two-letter country code');
const stateCode = z.string().trim().toUpperCase().regex(/^[A-Z0-9_-]{1,40}$/, 'Not a state code');

export const investmentScopeInputSchema = z
  .object({
    kind: z.enum(INVESTMENT_SCOPE_KINDS),
    country: countryCode.nullish(),
    state: stateCode.nullish(),
    regionName: shortTextSchema.nullish(),
    /** A region's countries, and states within them. A country with no state is the whole country. */
    areas: z.array(z.object({ country: countryCode, state: stateCode.nullish() })).max(200).optional(),
  })
  .superRefine((v, ctx) => {
    if ((v.kind === 'country' || v.kind === 'state') && !v.country) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['country'], message: 'Choose the country' });
    }
    if (v.kind === 'state' && !v.state) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['state'], message: 'Choose the state or province' });
    }
    if (v.kind === 'region') {
      if (!v.regionName) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['regionName'], message: 'Name the region' });
      if (!v.areas?.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['areas'], message: 'Choose the countries or states the region includes' });
      }
    }
  });
export type InvestmentScopeInput = z.infer<typeof investmentScopeInputSchema>;

export const investmentScopeViewSchema = z.object({
  kind: z.enum(INVESTMENT_SCOPE_KINDS),
  country: z.string().nullable(),
  state: z.string().nullable(),
  regionName: z.string().nullable(),
  areas: z.array(z.object({ country: z.string(), state: z.string().nullable() })),
  /** "Worldwide", "Nigeria", "Nigeria → Enugu", "South-East (NG-EN, NG-AN)". */
  label: z.string(),
});
export type InvestmentScopeView = z.infer<typeof investmentScopeViewSchema>;

// ── Writing an investment ──────────────────────────────────────────────────

const percentSchema = z
  .union([z.string(), z.number()])
  .transform((v) => String(v).trim())
  .refine((v) => /^\d{1,3}(\.\d{1,6})?$/.test(v) && Number(v) > 0 && Number(v) <= 100, 'Between 0 and 100, up to six decimals');

export const investmentFieldsSchema = z.object({
  status: z.enum(INVESTMENT_STATUSES).optional(),
  reference: z.string().trim().min(1).max(60).nullish(),
  amountInvested: nonNegativeMoneySchema.nullish(),
  currencyCode: currencyCodeSchema.nullish(),
  startsOn: isoDateSchema.nullish(),
  endsOn: isoDateSchema.nullish(),
  notes: longTextSchema.nullish(),
  scope: investmentScopeInputSchema.optional(),
  calculationPeriod: z.enum(CALCULATION_PERIODS).nullish(),
  payoutFrequency: z.enum(PAYOUT_FREQUENCIES).nullish(),
  // Vehicle partnership.
  vehiclePayoutBasis: z.enum(VEHICLE_PAYOUT_BASES).nullish(),
  vehicleFixedAmount: nonNegativeMoneySchema.nullish(),
  vehiclePercent: percentSchema.nullish(),
  // Equity.
  entityId: uuidSchema.nullish(),
  ownershipPercent: percentSchema.nullish(),
  ownershipBasis: shortTextSchema.nullish(),
  // Profit sharing.
  profitPercent: percentSchema.nullish(),
  operationalScope: longTextSchema.nullish(),
  eligibleIncome: z.array(z.enum(PROFIT_INCOME_STREAMS)).max(PROFIT_INCOME_STREAMS.length).nullish(),
  deductibleExpenses: z.enum(DEDUCTIBLE_EXPENSE_MODES).nullish(),
  deductibleCategoryIds: z.array(uuidSchema).max(200).nullish(),
  lossTreatment: z.enum(LOSS_TREATMENTS).nullish(),
  /** Confirms a profit share over places the same investor already shares profit in. */
  overlapAcknowledged: z.boolean().optional(),
});

export const investmentCreateSchema = investmentFieldsSchema.extend({
  investorId: uuidSchema,
  type: z.enum(INVESTMENT_TYPES),
});
export type InvestmentCreateInput = z.infer<typeof investmentCreateSchema>;

/**
 * Changing an investment. Terms that decide money already posted — dates,
 * percentages — go through a correction with a preview instead, when posted
 * distributions depend on them; the server says so.
 */
export const investmentUpdateSchema = investmentFieldsSchema;
export type InvestmentUpdateInput = z.infer<typeof investmentUpdateSchema>;

// ── Reading investments ────────────────────────────────────────────────────

export const investmentVehicleSchema = z.object({
  bikeId: uuidSchema,
  registration: z.string(),
  category: z.string(),
  from: z.string(),
  to: z.string().nullable(),
  current: z.boolean(),
  riderName: z.string().nullable(),
  /** "₦10,000.00 a week" or "25% of remittance". */
  share: z.string(),
});

/** Money for one investment, in its own currency. Kinds keep their own figures. */
export const investmentMoneySchema = z.object({
  currencyCode: z.string(),
  /** Vehicle partnership: the five figures, as for the investor's vehicles. */
  vehicle: investorFiguresSchema.nullable(),
  /** Equity and profit sharing: allocated by posted distributions, paid, and still owed. */
  allocated: moneySchema.nullable(),
  paid: moneySchema.nullable(),
  outstanding: moneySchema.nullable(),
});
export type InvestmentMoney = z.infer<typeof investmentMoneySchema>;

export const investmentViewSchema = z.object({
  id: uuidSchema,
  investorId: uuidSchema,
  investorName: z.string(),
  type: z.enum(INVESTMENT_TYPES),
  status: z.enum(INVESTMENT_STATUSES),
  reference: z.string().nullable(),
  amountInvested: moneySchema.nullable(),
  currencyCode: z.string().nullable(),
  startsOn: z.string().nullable(),
  endsOn: z.string().nullable(),
  notes: z.string().nullable(),
  agreement: z.object({ id: uuidSchema, filename: z.string() }).nullable(),
  scope: investmentScopeViewSchema,
  calculationPeriod: z.enum(CALCULATION_PERIODS).nullable(),
  payoutFrequency: z.enum(PAYOUT_FREQUENCIES).nullable(),
  vehiclePayoutBasis: z.enum(VEHICLE_PAYOUT_BASES).nullable(),
  vehicleFixedAmount: moneySchema.nullable(),
  vehiclePercent: z.string().nullable(),
  entity: z.object({ id: uuidSchema, name: z.string() }).nullable(),
  ownershipPercent: z.string().nullable(),
  ownershipBasis: z.string().nullable(),
  profitPercent: z.string().nullable(),
  operationalScope: z.string().nullable(),
  eligibleIncome: z.array(z.enum(PROFIT_INCOME_STREAMS)).nullable(),
  deductibleExpenses: z.enum(DEDUCTIBLE_EXPENSE_MODES).nullable(),
  deductibleCategories: z.array(z.object({ id: uuidSchema, name: z.string() })),
  lossTreatment: z.enum(LOSS_TREATMENTS).nullable(),
  overlapAcknowledged: z.boolean(),
  vehicles: z.array(investmentVehicleSchema),
  money: investmentMoneySchema,
  /** Details the agreement should have that are not recorded yet. */
  missing: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type InvestmentView = z.infer<typeof investmentViewSchema>;

export const investmentListQuerySchema = z.object({
  type: z.enum(INVESTMENT_TYPES).optional(),
  status: z.enum(INVESTMENT_STATUSES).optional(),
  investorId: uuidSchema.optional(),
  search: z.string().trim().max(120).optional(),
  scopeKind: z.enum(INVESTMENT_SCOPE_KINDS).optional(),
  /** Investments that apply in this country (worldwide ones included). */
  country: countryCode.optional(),
  /** …and this state within it. */
  state: stateCode.optional(),
  /** In force at some point between these dates. */
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
});
export type InvestmentListQuery = z.infer<typeof investmentListQuerySchema>;

/** A summary card: one kind of investment in one currency. Never mixed. */
export const investmentSummaryCardSchema = z.object({
  type: z.enum(INVESTMENT_TYPES),
  currencyCode: z.string().nullable(),
  count: z.number().int(),
  active: z.number().int(),
  invested: moneySchema,
  /** Vehicle: collected for investors. Equity and profit: allocated. */
  earned: moneySchema,
  paid: moneySchema,
  outstanding: moneySchema,
});
export type InvestmentSummaryCard = z.infer<typeof investmentSummaryCardSchema>;

export const investmentListSchema = z.object({
  rows: z.array(investmentViewSchema),
  summary: z.array(investmentSummaryCardSchema),
  /** Investments with no amount or currency recorded are counted, not summed. */
  incomplete: z.number().int(),
});
export type InvestmentList = z.infer<typeof investmentListSchema>;

// ── One investment in full ─────────────────────────────────────────────────

export const investmentAllocationViewSchema = z.object({
  id: uuidSchema,
  distributionId: uuidSchema,
  kind: z.enum(['allocation', 'adjustment']),
  distributionKind: z.enum(['dividend', 'profit_share']),
  /** "Dividend, holders on 2026-06-30" or "Profit share 2026-07-01 – 2026-09-30". */
  label: z.string(),
  effectiveOn: z.string(),
  amount: moneySchema,
  reason: z.string().nullable(),
  voided: z.boolean(),
});

export const investmentPaymentViewSchema = z.object({
  id: uuidSchema,
  amount: moneySchema,
  currencyCode: z.string(),
  paidOn: z.string(),
  method: z.string(),
  accountName: z.string().nullable(),
  reference: z.string().nullable(),
  note: z.string().nullable(),
  recordedBy: z.string().nullable(),
  voided: z.boolean(),
  voidReason: z.string().nullable(),
});
export type InvestmentPaymentView = z.infer<typeof investmentPaymentViewSchema>;

export const investmentHistoryEntrySchema = z.object({
  at: z.string(),
  by: z.string().nullable(),
  summary: z.string(),
});

export const investmentDetailSchema = investmentViewSchema.extend({
  allocations: z.array(investmentAllocationViewSchema),
  payments: z.array(investmentPaymentViewSchema),
  /** Vehicle partnership payouts (the vehicle ledger), for this investment. */
  vehiclePayouts: z.array(
    z.object({ id: uuidSchema, amount: moneySchema, paidOn: z.string(), method: z.string(), reference: z.string().nullable(), voided: z.boolean() }),
  ),
  history: z.array(investmentHistoryEntrySchema),
});
export type InvestmentDetail = z.infer<typeof investmentDetailSchema>;

// ── Entities ───────────────────────────────────────────────────────────────

export const investmentEntityInputSchema = z.object({
  name: shortTextSchema,
  registrationNumber: z.string().trim().min(1).max(80).nullish(),
  countryCode: countryCode.nullish(),
  note: longTextSchema.nullish(),
});
export type InvestmentEntityInput = z.infer<typeof investmentEntityInputSchema>;

export const investmentEntityViewSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  registrationNumber: z.string().nullable(),
  countryCode: z.string().nullable(),
  note: z.string().nullable(),
  /** Recorded equity in force today, by ownership basis — never added across entities. */
  ownership: z.array(z.object({ basis: z.string(), percent: z.string() })),
});
export type InvestmentEntityView = z.infer<typeof investmentEntityViewSchema>;

// ── Dividends ──────────────────────────────────────────────────────────────

export const dividendInputSchema = z.object({
  entityId: uuidSchema,
  ownershipBasis: shortTextSchema,
  /** Holders of record on this date receive it. */
  recordDate: isoDateSchema,
  declaredOn: isoDateSchema,
  currencyCode: currencyCodeSchema,
  /** The whole amount the entity declared, to every holder on that basis. */
  totalAmount: positiveMoneySchema,
  note: longTextSchema.nullish(),
  idempotencyKey: z.string().trim().min(8).max(100).optional(),
});
export type DividendInput = z.infer<typeof dividendInputSchema>;

export const distributionAllocationLineSchema = z.object({
  investmentId: uuidSchema,
  investorId: uuidSchema,
  investorName: z.string(),
  percent: z.string(),
  amount: moneySchema,
});

export const dividendResultSchema = z.object({
  applied: z.boolean(),
  replayed: z.boolean(),
  distributionId: uuidSchema.nullable(),
  entity: z.object({ id: uuidSchema, name: z.string() }),
  ownershipBasis: z.string(),
  recordDate: z.string(),
  currencyCode: z.string(),
  totalAmount: moneySchema,
  allocations: z.array(distributionAllocationLineSchema),
  allocatedTotal: moneySchema,
  /** Belongs to holders not recorded here (the company, founders). Not allocated. */
  unallocated: moneySchema,
  flags: z.array(z.string()),
});
export type DividendResult = z.infer<typeof dividendResultSchema>;

// ── Profit shares ──────────────────────────────────────────────────────────

export const profitShareInputSchema = z.object({
  periodStart: isoDateSchema,
  periodEnd: isoDateSchema,
  declaredOn: isoDateSchema.optional(),
  note: longTextSchema.nullish(),
  idempotencyKey: z.string().trim().min(8).max(100).optional(),
});
export type ProfitShareInput = z.infer<typeof profitShareInputSchema>;

export const profitBreakdownSchema = z.object({
  currencyCode: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  /** The locations whose transactions are counted. */
  locations: z.array(z.object({ id: uuidSchema, name: z.string() })),
  /** Worldwide only: business-wide income and costs with no location (remittance) count too. */
  includesBusinessWide: z.boolean(),
  income: z.array(z.object({ stream: z.enum(PROFIT_INCOME_STREAMS), label: z.string(), amount: moneySchema, note: z.string().nullable() })),
  expenses: z.array(z.object({ categoryId: uuidSchema.nullable(), name: z.string(), amount: moneySchema })),
  /** Income and expenses in other currencies: not converted, not counted. */
  otherCurrencies: z.array(z.object({ currencyCode: z.string(), income: moneySchema, expenses: moneySchema })),
  incomeTotal: moneySchema,
  expenseTotal: moneySchema,
  profit: moneySchema,
  lossBroughtForward: moneySchema,
  profitAfterLosses: moneySchema,
  percent: z.string(),
  share: moneySchema,
  lossCarriedOut: moneySchema,
  lossTreatment: z.enum(LOSS_TREATMENTS),
  /** Other profit shares over some of the same places and dates. */
  overlapping: z.array(z.object({ investmentId: uuidSchema, investorName: z.string(), percent: z.string(), scope: z.string() })),
});
export type ProfitBreakdown = z.infer<typeof profitBreakdownSchema>;

export const profitShareResultSchema = z.object({
  applied: z.boolean(),
  replayed: z.boolean(),
  distributionId: uuidSchema.nullable(),
  breakdown: profitBreakdownSchema,
  flags: z.array(z.string()),
});
export type ProfitShareResult = z.infer<typeof profitShareResultSchema>;

/** The next period a profit share can be posted for, by the agreement. */
export const profitShareNextSchema = z.object({
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  /** Why none can be posted yet, when one cannot. */
  blocked: z.string().nullable(),
});

// ── Voiding ────────────────────────────────────────────────────────────────

export const investmentVoidSchema = z.object({ reason: shortTextSchema });

// ── Paying equity and profit-sharing investors ────────────────────────────

export const investmentPaymentInputSchema = z.object({
  amount: positiveMoneySchema,
  paidOn: isoDateSchema,
  method: z.enum(PAYMENT_METHODS),
  accountId: uuidSchema.nullish(),
  reference: z.string().trim().min(1).max(120).nullish(),
  note: longTextSchema.nullish(),
  idempotencyKey: z.string().trim().min(8).max(100),
  confirmDuplicate: z.boolean().optional(),
});
export type InvestmentPaymentInput = z.infer<typeof investmentPaymentInputSchema>;

// ── Correcting dates and terms after money was posted ─────────────────────

/** Equity and profit sharing: dates and percentages, recalculated over posted distributions. */
export const investmentCorrectionInputSchema = z.object({
  startsOn: isoDateSchema.optional(),
  endsOn: isoDateSchema.nullish(),
  ownershipPercent: percentSchema.optional(),
  profitPercent: percentSchema.optional(),
  reason: shortTextSchema,
  idempotencyKey: z.string().trim().min(8).max(100).optional(),
});
export type InvestmentCorrectionInput = z.infer<typeof investmentCorrectionInputSchema>;

export const investmentCorrectionResultSchema = z.object({
  applied: z.boolean(),
  replayed: z.boolean(),
  changed: z.boolean(),
  batchId: uuidSchema.nullable(),
  before: z.record(z.string(), z.string().nullable()),
  after: z.record(z.string(), z.string().nullable()),
  /** Each posted distribution the change reaches: what it allocated, what it should, the difference. */
  distributions: z.array(
    z.object({
      distributionId: uuidSchema,
      label: z.string(),
      before: moneySchema,
      after: moneySchema,
      difference: moneySchema,
      lossCarriedBefore: moneySchema.nullable(),
      lossCarriedAfter: moneySchema.nullable(),
    }),
  ),
  totalDifference: moneySchema,
  currencyCode: z.string().nullable(),
  flags: z.array(z.string()),
});
export type InvestmentCorrectionResult = z.infer<typeof investmentCorrectionResultSchema>;

/** Vehicle partnership: new share terms for every linked vehicle, from a date. */
export const vehicleTermsChangeInputSchema = z
  .object({
    basis: z.enum(VEHICLE_PAYOUT_BASES),
    fixedAmount: nonNegativeMoneySchema.optional(),
    percent: percentSchema.optional(),
    effectiveFrom: isoDateSchema,
    reason: shortTextSchema,
    idempotencyKey: z.string().trim().min(8).max(100).optional(),
  })
  .refine((v) => (v.basis === 'fixed_per_period' ? v.fixedAmount !== undefined : v.percent !== undefined), {
    path: ['fixedAmount'],
    message: 'Enter the amount or percentage',
  });
export type VehicleTermsChangeInput = z.infer<typeof vehicleTermsChangeInputSchema>;

export const vehicleTermsChangeResultSchema = z.object({
  applied: z.boolean(),
  replayed: z.boolean(),
  changed: z.boolean(),
  vehicles: z.array(linkCorrectionResultSchema),
  figuresBefore: investorFiguresSchema,
  figuresAfter: investorFiguresSchema,
  companyRevenueAdjustment: moneySchema,
  flags: z.array(z.string()),
});
export type VehicleTermsChangeResult = z.infer<typeof vehicleTermsChangeResultSchema>;

// ── Who may manage investments ─────────────────────────────────────────────

export const INVESTMENT_PERMISSIONS = ['investments:read', 'investments:manage', 'investments:post'] as const;
export type InvestmentPermission = (typeof INVESTMENT_PERMISSIONS)[number];
export const investmentAccessInputSchema = z.object({
  permissions: z.array(z.enum(INVESTMENT_PERMISSIONS)),
});
